// Mirrors backend project/apps/modules/shipturtle/products/option_utils.py
// Canonical variant-option shape: [{ optionName, name }, ...]

const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

export function normalizeVariantOptions(meta) {
  if (!meta || !isPlainObject(meta)) return [];

  const raw = meta.option_values ?? meta.optionValues ?? meta.options;
  const result = [];

  if (Array.isArray(raw) && raw.length > 0) {
    for (const entry of raw) {
      if (!isPlainObject(entry)) continue;
      const optName = String(entry.optionName ?? entry.option_name ?? '').trim();
      const optValue = String(
        entry.name ?? entry.value ?? entry.option_value ?? ''
      ).trim();
      if (optName && optValue) result.push({ optionName: optName, name: optValue });
    }
  } else if (isPlainObject(raw)) {
    for (const [key, val] of Object.entries(raw)) {
      const optName = String(key ?? '').trim();
      const optValue = String(val ?? '').trim();
      if (optName && optValue) result.push({ optionName: optName, name: optValue });
    }
  }

  if (result.length === 0) {
    for (let i = 1; i <= 3; i += 1) {
      const optValue = meta[`option${i}`];
      const optName = meta[`option${i}_name`];
      if (optValue && optName) {
        const v = String(optValue).trim();
        const n = String(optName).trim();
        if (v && v.toLowerCase() !== 'default title') {
          result.push({ optionName: n, name: v });
        }
      }
    }
  }

  return result;
}

// Look up a single option value by name across any storage shape (list, dict, legacy).
// Pass either a variant ({ option_values, meta }) or a plain meta object.
export function getOptionValue(source, optionName) {
  if (!source || !optionName) return undefined;
  const meta = source.option_values !== undefined || source.meta !== undefined
    ? { ...(source.meta || {}), ...(source.option_values !== undefined ? { option_values: source.option_values } : {}) }
    : source;
  const normalized = normalizeVariantOptions(meta);
  const target = String(optionName).trim();
  const match = normalized.find((o) => o.optionName === target);
  return match ? match.name : undefined;
}

export function buildOptionValuesFromCombo(combo) {
  if (!Array.isArray(combo)) return [];
  return combo
    .map((c) => ({
      optionName: String(c.optionName ?? '').trim(),
      name: String(c.value ?? '').trim(),
    }))
    .filter((o) => o.optionName && o.name);
}

// Build canonical list from an in-order array of values aligned to an array of option specs.
// Used where the option spec is { name } and values are positional.
export function buildOptionValuesFromOptionsAndCombo(options, combo) {
  if (!Array.isArray(options) || !Array.isArray(combo)) return [];
  return options
    .map((opt, i) => ({
      optionName: String(opt?.name ?? '').trim(),
      name: String(combo[i] ?? '').trim(),
    }))
    .filter((o) => o.optionName && o.name);
}

export function generateVariantName(optionValues) {
  if (!Array.isArray(optionValues) || optionValues.length === 0) return '';
  return optionValues
    .map((o) => String(o.name ?? '').trim())
    .filter((v) => v && v.toLowerCase() !== 'default title')
    .join(' | ');
}

// Product-level options [{ name, position, values }] for the editor.
// `product.options` is built by the backend from the relational option tables (the
// source of truth); `product.meta.options` is the legacy JSON copy, used only when
// the API returned no options from the tables.
export function getProductOptions(product) {
  const fromTables = Array.isArray(product?.options)
    ? product.options.filter((opt) => opt?.name)
    : [];
  if (fromTables.length > 0) return fromTables;
  return (product?.meta?.options || []).filter((opt) => opt?.name);
}
